import { useCallback } from 'react'
import debounce from 'lodash.debounce'
import useCart from './use-cart'
import {
  getCartId,
  cartLineItemUpdateMutation,
  normalizeShopifyCart,
} from '../utils'
import fetcherFn from '../utils/fetcher'
import type { HookFetcherFn } from '../utils/types'
import type { MaybeCart, LineItem, UpdateItemHook } from '../types/cart'

export type UpdateItemActionInput<T = any> = T extends LineItem
  ? Partial<UpdateItemHook['actionInput']>
  : UpdateItemHook['actionInput']

export type UpdateItemFn<T = any> = (
  input: UpdateItemActionInput<T>
) => Promise<MaybeCart>

export type UseUpdateItem = <T extends LineItem | undefined = undefined>(ctx?: {
  item?: T
  wait?: number
}) => (input: Partial<T>) => Promise<MaybeCart>

export type UpdateItemFetcher = HookFetcherFn<UpdateItemHook>

const useUpdateItem: UseUpdateItem = (ctx = {}) => {
  const { item, wait } = ctx
  const { mutate } = useCart()

  const hookFetcher: UpdateItemFetcher = async (hookFetcherContext) => {
    const {
      options,
      fetch,
      input: { itemId, item },
    } = hookFetcherContext
    const cartId = getCartId()
    const lineItems = [
      {
        id: itemId,
        quantity: item.quantity,
        attributes: item.attributes || [],
        sellingPlanId: item.sellingPlanId || null,
      },
    ]
    const variables = { cartId, lineItems }
    const { cartLinesUpdate } = await fetch({ ...options, variables })

    return normalizeShopifyCart(cartLinesUpdate.cart)
  }

  const updateItem: UpdateItemFn<LineItem> = async (input) => {
    const itemId = input.id ?? item?.id
    const productId = input.productId ?? item?.productId
    const variantId = input.productId ?? item?.variantId

    if (!itemId || !productId || !variantId) {
      throw new Error('Invalid input used for this operation')
    }

    const hookFetcherContext = {
      input: {
        itemId,
        item: {
          productId,
          variantId,
          quantity: input.quantity,
          attributes: item?.attributes,
          sellingPlanId: item?.sellingPlanId,
        },
      },
      fetch: fetcherFn,
      options: { query: cartLineItemUpdateMutation },
    }
    const data = await hookFetcher(hookFetcherContext)
    await mutate(data, false)
    return data
  }

  const debouncedUpdateItem = debounce(
    updateItem,
    wait ?? 500
  ) as UpdateItemFn<LineItem>
  return useCallback(debouncedUpdateItem, [mutate, debouncedUpdateItem])
}

export default useUpdateItem
